/**
 * Form-only view-model types for the SSO settings page.
 * Server response shapes come exclusively from generated codegen output.
 */

import type { OrganizationSsoFieldsFragment } from "../../graphql/generated";

export interface SSOFormState {
  enabled: boolean;
  enforced: boolean;
  emailDomains: string[];
  google: {
    enabled: boolean;
    hostedDomain: string;
    allowJIT: boolean;
  };
  microsoft: {
    enabled: boolean;
    tenantId: string;
    allowJIT: boolean;
  };
}

export type SSOFormErrorKey =
  | "tenantIdRequired"
  | "tenantIdInvalid"
  | "hostedDomainRequired"
  | "hostedDomainInvalid"
  | "domainInvalid"
  | "enforceRequiresEnabled"
  | "atLeastOneProvider"
  | "enforceRequiresDomain";

export interface SSOFormErrors {
  microsoftTenantId?: SSOFormErrorKey;
  googleHostedDomain?: SSOFormErrorKey;
  emailDomains?: SSOFormErrorKey;
  /** Cross-field / business-rule error. */
  form?: SSOFormErrorKey;
}

export const EMPTY_SSO_FORM_STATE: SSOFormState = {
  enabled: false,
  enforced: false,
  emailDomains: [],
  google: { enabled: false, hostedDomain: "", allowJIT: false },
  microsoft: { enabled: false, tenantId: "", allowJIT: false },
};

export function ssoFragmentToFormState(
  sso: OrganizationSsoFieldsFragment | null | undefined,
): SSOFormState {
  if (!sso) return EMPTY_SSO_FORM_STATE;
  return {
    enabled: sso.enabled,
    enforced: sso.enforced,
    emailDomains: [...sso.emailDomains],
    google: {
      enabled: !!sso.google,
      hostedDomain: sso.google?.hostedDomain ?? "",
      allowJIT: sso.google?.allowJIT ?? false,
    },
    microsoft: {
      enabled: !!sso.microsoft,
      tenantId: sso.microsoft?.tenantId ?? "",
      allowJIT: sso.microsoft?.allowJIT ?? false,
    },
  };
}
